import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { AccountApi } from 'src/app/shared/apis/account.api';
import { AuthApi } from 'src/app/shared/apis/auth.api';
import { KycApi } from 'src/app/shared/apis/kyc-basic.api';
import { ResetPassWordApi } from 'src/app/shared/apis/reset-password.api';
import { ResourceApi } from 'src/app/shared/apis/resource.api';
import {
  Need2Fa,
  RegisterByEmailParams,
  ResetPwdVerifyByEmailParams,
  SendAction,
  ThirdAuthUser,
  VerifyAction,
  VerifyEmailParam,
  VerifyGoogleParam,
  VerifyPhoneParam,
  Web3WalletType,
} from 'src/app/shared/interfaces/auth.interface';
import { KycLiteForm } from 'src/app/shared/interfaces/kyc.interface';
import { DynamicAd } from 'src/app/shared/interfaces/resource.interface';
import { ResponseData } from 'src/app/shared/interfaces/response.interface';
import { MatConnectedDialogMobileAnimation } from 'src/app/shared/service/connected-dialog';
import { DataCollectionService } from 'src/app/shared/service/data-collection.service';
import { LoginDialog } from 'src/app/shared/service/dialog-loaders';
import { LazyDialogService } from 'src/app/shared/service/lazy-dialog.service';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { environment } from 'src/environments/environment';
import { CsPopupService } from '../../shared/components/cs-popup/cs-popup.service';
// 注意：此處刻意不 import LoginDialogComponent。
// 它會連鎖靜態 import LoginRegisterComponent -> ThirdAuthM8Component
// -> ThirdAuthM8Service -> Web3WalletService，使整套 Web3 SDK
// （ethers / viem / TonConnect / AppKit）進入 initial bundle。
// 下方三個 open* 方法改經 LazyDialogService + dialog-loaders 於開啟彈窗時才載入。

export enum DialogType {
  LOGIN_REGISTER = 'login_register',
  REGISTER_AGREE = 'register_agree',
  LOGIN_2FA = 'login_2fa',
  RESET_PASSWORD = 'reset_password',
  RESET_PASSWORD_EMAIL = 'reset_password_email',
  THIRD_PARTY = 'third_party',
  KYC = 'kyc',
}

export interface LogIn2faData {
  need2fa: Need2Fa;
  email: string;
  uniCode: string;
}

export interface ThirdParty2faData {
  need2fa: Need2Fa;
  email: string;
  uniCode: string;
  areaCode: string;
}

export interface SignUp2faData {
  need2fa: Need2Fa;
  email: string;
  uniCode: string;
  referee: string;
  myAff: string;
  password: string;
}

export const CAMPAIGN_REDIRECT_FALLBACK_STORAGE_KEY = 'campaignRedirectFallback';

@Injectable({ providedIn: 'root' })
export class LoginService {
  constructor(
    private lazyDialog: LazyDialogService,
    private dataCollectionService: DataCollectionService,
    private appService: AppService,
    private authApi: AuthApi,
    private kycApi: KycApi,
    private resetPassWordApi: ResetPassWordApi,
    private toast: ToastService,
    private localeService: LocaleService,
    private localstorageService: LocalStorageService,
    private csPopupService: CsPopupService,
    private accountApi: AccountApi,
    private resourceApi: ResourceApi,
  ) {}

  public dialogType$: BehaviorSubject<DialogType> = new BehaviorSubject<DialogType>(DialogType.LOGIN_REGISTER);

  public thirdPartyType$: BehaviorSubject<Web3WalletType> = new BehaviorSubject<Web3WalletType>(
    Web3WalletType.Coinbase,
  );

  public otpType: VerifyAction = 'Login';

  public user2fa!: Need2Fa;

  public email: string = '';

  public emailCode: string = '';

  public password: string = '';

  public uniCode: string = '';

  public referee: string = '';

  public myAff: string = '';

  public areaCode: string = '';

  public mobile: string = '';

  public login2faSendType: SendAction = 'Email';

  public login2faHeader = 'Email Verification';

  public login2faSubmitText = 'Confirm Email';

  public needKyc: boolean = false;

  public kycFormValue: KycLiteForm | null = null;

  public campaignAd: DynamicAd | null = null;

  public isCampaignImageLoading: boolean = false;

  private hasCampaign: boolean = false;

  private campaignId: string = '';

  private pendingAuthLogged: (() => void) | null = null;

  private kycFlagPromise: Promise<boolean> | null = null;

  public initCampaignImage(id: string | null): void {
    if (!id) {
      this.resetCampaignImage();
      return;
    }

    if (this.campaignId === id && (this.isCampaignImageLoading || this.campaignAd)) return;

    this.hasCampaign = true;
    this.campaignId = id;
    this.campaignAd = null;
    this.isCampaignImageLoading = true;

    this.resourceApi.getDynamicAd(id).subscribe({
      next: res => {
        if (this.campaignId !== id) return;
        this.campaignAd = res?.data || null;
        this.isCampaignImageLoading = false;
        this.runPendingAuthLogged();
      },
      error: () => {
        if (this.campaignId !== id) return;
        this.campaignAd = null;
        this.isCampaignImageLoading = false;
        this.runPendingAuthLogged();
      },
    });
  }

  public getCampaignImageUrl(isH5: boolean): string {
    const imageUrl = isH5
      ? this.campaignAd?.mobileImg || this.campaignAd?.webImg
      : this.campaignAd?.webImg || this.campaignAd?.mobileImg;
    return this.normalizeImageUrl(imageUrl || '');
  }

  public logged(token: string, checkRegBonus: boolean = false, checkUserDeposit: boolean = true): void {
    const logged = () => this.appService.logged(token, checkRegBonus, checkUserDeposit);

    if (!this.hasCampaign) {
      logged();
      return;
    }

    if (this.isCampaignImageLoading) {
      this.pendingAuthLogged = logged;
      return;
    }

    this.redirectAfterAuth(logged);
  }

  public async refreshNeedKycFlag(): Promise<boolean> {
    if (this.localstorageService.forceKycFlag !== null) {
      this.needKyc = this.localstorageService.forceKycFlag;
      return this.needKyc;
    }

    //測試環境使用變數
    // if (this.appService.tenantConfig$.value?.config?.kycEnabled) {
    //   this.needKyc = this.appService.tenantConfig$.value?.config?.kycEnabled === 'true';
    //   return this.needKyc;
    // }

    if (!this.kycFlagPromise) {
      this.kycFlagPromise = (async () => {
        try {
          const response = await firstValueFrom(this.kycApi.getIsForceKyc());
          return response?.data?.isForceKyc ?? false;
        } catch (error) {
          console.error(error);
          return false;
        } finally {
          this.kycFlagPromise = null;
        }
      })();
    }

    this.needKyc = await this.kycFlagPromise;
    this.localstorageService.forceKycFlag = this.needKyc;
    return this.needKyc;
  }

  public async triggerLiteKyc(email: string, otpCode?: string): Promise<void> {
    const payload: KycLiteForm = {};
    this.localstorageService.kycLitePendingToast = null;
    if (this.kycFormValue) {
      Object.assign(payload, this.kycFormValue);
    }
    if (email) {
      payload.email = email;
    }
    const errorMsg = 'KYC submission failed. Please submit your application again.';
    try {
      const res = await firstValueFrom(this.kycApi.setKycLite(payload, otpCode));
      if (!res?.success) {
        this.localstorageService.kycLitePendingToast = {
          message: errorMsg,
          type: 'fail',
        };
      }
    } catch (error: any) {
      this.localstorageService.kycLitePendingToast = {
        message: errorMsg,
        type: 'fail',
      };
      console.error(error);
    }
  }

  public handleLogin2fa(data: LogIn2faData): void {
    this.otpType = 'Login';
    const { need2fa, email, uniCode } = data;
    this.user2fa = need2fa;
    this.email = email;
    this.uniCode = uniCode;
    this.mobile = need2fa?.mobile || '';
    this.areaCode = need2fa?.areaCode || '';
    this.updateLogin2faSendType(this.getLogin2faSendType(need2fa));
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public handleThridParty2Fa(data: ThirdParty2faData): void {
    this.otpType = 'Login';
    const { need2fa, email, uniCode, areaCode } = data;
    this.user2fa = need2fa;
    this.email = email;
    this.uniCode = uniCode;
    this.areaCode = need2fa?.areaCode || areaCode;
    this.mobile = need2fa?.mobile || '';
    this.updateLogin2faSendType(this.getLogin2faSendType(need2fa));
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public async handleRegister2fa(data: SignUp2faData) {
    this.otpType = 'Register';
    const { need2fa, email, uniCode, referee, myAff, password } = data;
    this.user2fa = need2fa;
    this.email = email;
    this.uniCode = uniCode;
    this.referee = referee;
    this.myAff = myAff;
    this.password = password;
    this.emailCode = '';
    this.mobile = '';
    this.areaCode = '';
    this.updateLogin2faSendType('Email');
    await this.refreshNeedKycFlag();
    this.dialogType$.next(this.needKyc ? DialogType.KYC : DialogType.REGISTER_AGREE);
  }

  public handleForgotPassword(): void {
    this.dialogType$.next(DialogType.RESET_PASSWORD_EMAIL);
  }

  public handleResetPasswordEmail(email: string): void {
    this.otpType = 'ResetPwd';
    this.email = email;
    this.mobile = '';
    this.areaCode = '';
    this.updateLogin2faSendType('Email');
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public async handleResetPassword(password: string, unicode: string) {
    const result: any = await firstValueFrom(this.resetPassWordApi.postResetPwd(password, unicode));
    if (result?.data) {
      //密码修改成功，跳转到登录
      this.toast.show({ message: this.localeService.getValue('pwd_change_success'), type: 'success' });
      this.reset();
      return;
    }
    //2013 禁用用户
    if (result?.code === '2013') {
      this.appService.showForbidTip('login');
      return;
    }
  }

  handleThirdPartyNotLogin(type: ThirdAuthUser) {
    switch (type) {
      case ThirdAuthUser.Coinbase:
        this.thirdPartyType$.next(Web3WalletType.Coinbase);
        this.dialogType$.next(DialogType.THIRD_PARTY);
        break;

      case ThirdAuthUser.MetaMask:
        this.thirdPartyType$.next(Web3WalletType.MetaMask);
        break;

      case ThirdAuthUser.CoinbaseWallet:
        this.thirdPartyType$.next(Web3WalletType.CoinbaseWallet);
        break;

      case ThirdAuthUser.WalletConnect:
        this.thirdPartyType$.next(Web3WalletType.WalletConnect);
        break;

      case ThirdAuthUser.TonConnect:
        this.thirdPartyType$.next(Web3WalletType.TonConnect);
        break;

      default:
        break;
    }
  }

  public async verify2fa(emailCode: string, verifyType: 'EMAIL' | 'GOOGLE' | 'MOBILE' = 'EMAIL'): Promise<boolean> {
    switch (this.otpType) {
      case 'Login':
        return await this._loginBy2Fa(this.email, emailCode, verifyType);
      case 'Register':
        this.emailCode = emailCode;
        await this._finalizeRegisterFlow();
        return true;
      case 'ResetPwd':
        return await this._resetPasswordBy2Fa(this.email, emailCode);
      default:
        return false;
    }
  }

  public reset(): void {
    this.dialogType$.next(DialogType.LOGIN_REGISTER);
    this.referee = '';
    this.myAff = '';
    this.email = '';
    this.password = '';
    this.uniCode = '';
    this.mobile = '';
    this.areaCode = '';
    this.emailCode = '';
    this.updateLogin2faSendType('Email');
    this.kycFormValue = null;
    this.resetCampaignImage();
  }

  public updateLogin2faSendType(sendType: SendAction): void {
    this.login2faSendType = sendType;
    this.setLogin2faText(sendType);
  }

  openLogin(): void {
    void this.lazyDialog.open(LoginDialog, {
      data: { tabIndex: 1 },
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });
  }

  openSelfExclusionAnnouncement() {
    this.csPopupService.info({ header: 'Self Exclusion active', message: 'Self Exclusion active', confirmText: 'OK' });
  }

  openRegister(): void {
    void this.lazyDialog.open(LoginDialog, {
      data: { tabIndex: 0 },
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });
  }

  openResetPassword(): void {
    this.dialogType$.next(DialogType.RESET_PASSWORD);
    void this.lazyDialog.open(LoginDialog, {
      data: { tabIndex: 1 },
      mobileAnimation: MatConnectedDialogMobileAnimation.SLIDE_UP,
      mobileFixedBottom: true,
    });
  }

  /**
   * 获取用户信息
   */
  async getUserInfo() {
    const accountInfo = await firstValueFrom(this.accountApi.getUserAccountInfo());
    if (accountInfo.success) {
      this.appService.userInfo$.next(accountInfo.data);
    }
  }

  /**
   * 接口返回 错误统一处理
   *
   * @param responseData
   * @param description reg_tip01 (该帐户已经被注册) sign_up_email (该邮箱已被注册，请重试)
   */
  onProcessError(responseData: ResponseData<string>) {
    this.toast.show({
      message: responseData?.message ? responseData.message : this.localeService.getValue('reg_f'),
      type: 'fail',
    });
  }

  private async _loginBy2Fa(
    email: string,
    code: string,
    verifyType: 'EMAIL' | 'GOOGLE' | 'MOBILE' = 'EMAIL',
  ): Promise<boolean> {
    try {
      let param: VerifyEmailParam | VerifyGoogleParam | VerifyPhoneParam;
      switch (verifyType) {
        case 'GOOGLE':
          param = { googleCode: code };
          break;
        case 'MOBILE':
          param = {
            areaCode: this.areaCode,
            mobile: this.mobile,
            otpCode: code,
            smsVoice: false,
          };
          break;
        case 'EMAIL':
        default:
          param = { email: email, emailCode: code };
          break;
      }
      const response = await firstValueFrom(this.authApi.post2faVerify(this.uniCode, param));

      // 2fa 验证后 获取token并替换
      if (response?.success) {
        this.dataCollectionService.gtmPush('login', { login_type: 'login' });
        this.logged(response?.data);
        return true;
      } else {
        this.onProcessError(response);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private async _registerBy2Fa(email: string, password: string, otpCode: string): Promise<boolean> {
    try {
      const param: RegisterByEmailParams = {
        email,
        password,
        otpCode,
        referrer: this.referee,
        aff: this.myAff,
      };
      const response = await firstValueFrom(this.authApi.emailRegister(param));
      if (response?.success) {
        if (this.needKyc) {
          this.appService.updateToken(response.data);
          await this.triggerLiteKyc(email);
        }
        this.localstorageService.isNewRegisterLogin = true;
        this.dataCollectionService.gtmPush('login', { login_type: 'register' });
        await this.pushSuccessRegistrationEvent();
        this.logged(response.data, true);
        return true;
      } else {
        this.onProcessError(response);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private async pushSuccessRegistrationEvent(): Promise<void> {
    try {
      this.localstorageService.registrationSuccessPending = true;
    } catch (error) {
      console.error('pushSuccessRegistrationEvent failed', error);
    }
  }

  private async _finalizeRegisterFlow(): Promise<void> {
    if (!this.emailCode) {
      this.toast.show({ message: 'Please complete verification first.', type: 'fail' });
      return;
    }
    const success = await this._registerBy2Fa(this.email, this.password, this.emailCode);
    if (success) {
      this.emailCode = '';
      this.kycFormValue = null;
    }
  }

  public async confirmRegisterAgreement(): Promise<void> {
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  public async submitKycForm(formValue: KycLiteForm): Promise<void> {
    this.kycFormValue = formValue;
    this.dialogType$.next(DialogType.LOGIN_2FA);
  }

  private runPendingAuthLogged(): void {
    if (!this.pendingAuthLogged) return;
    this.redirectAfterAuth(this.pendingAuthLogged);
  }

  private redirectAfterAuth(logged: () => void): void {
    this.pendingAuthLogged = null;
    const redirectUrl = this.campaignAd?.redirectUrl || '';
    this.localstorageService.pendingLoginRedirectUrl = redirectUrl
      ? this.getCampaignRedirectUrl(redirectUrl)
      : `/${this.appService.languageCode}`;
    logged();
  }

  private getCampaignRedirectUrl(redirectUrl: string): string {
    const localizedUrl = this.getLocalizedRedirectUrl(redirectUrl);
    this.updateCampaignRedirectFallback(localizedUrl);
    return localizedUrl;
  }

  private getLocalizedRedirectUrl(redirectUrl: string): string {
    if (/^https?:\/\//i.test(redirectUrl)) return redirectUrl;

    return redirectUrl.startsWith(`/${this.appService.languageCode}`)
      ? redirectUrl
      : `/${this.appService.languageCode}${redirectUrl.startsWith('/') ? '' : '/'}${redirectUrl}`;
  }

  private updateCampaignRedirectFallback(url: string): void {
    sessionStorage.removeItem(CAMPAIGN_REDIRECT_FALLBACK_STORAGE_KEY);

    try {
      const parsedUrl = new URL(url, window.location.origin);
      if (parsedUrl.origin !== window.location.origin) return;

      sessionStorage.setItem(CAMPAIGN_REDIRECT_FALLBACK_STORAGE_KEY, 'home');
    } catch {
      sessionStorage.removeItem(CAMPAIGN_REDIRECT_FALLBACK_STORAGE_KEY);
    }
  }

  private normalizeImageUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('assets/')) return url;
    if (url.startsWith('/assets/')) return url;

    return `${environment.resourceUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  private resetCampaignImage(): void {
    this.campaignAd = null;
    this.isCampaignImageLoading = false;
    this.hasCampaign = false;
    this.campaignId = '';
    this.pendingAuthLogged = null;
  }

  private async _resetPasswordBy2Fa(email: string, emailCode: string): Promise<boolean> {
    try {
      const param: ResetPwdVerifyByEmailParams = { email, emailCode, otpType: 'ResetPwd' };
      const response: any = await firstValueFrom(this.resetPassWordApi.onResetPwdVerifyByEmail(param));
      if (response?.success) {
        if (!response?.data?.isExisted) {
          this.toast.show({
            message: 'Email is not registered',
            type: 'fail',
          });
          return false;
        }

        this.toast.show({ message: this.localeService.getValue('reset_pwd_success'), type: 'success' });
        this.uniCode = response?.data?.uniCode;
        this.emailCode = emailCode;
        this.dialogType$.next(DialogType.RESET_PASSWORD);
        return true;
      } else {
        this.onProcessError(response);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private getLogin2faSendType(need2fa: Need2Fa): SendAction {
    const supportOtpMethod = need2fa?.supportOtpMethod?.length
      ? need2fa.supportOtpMethod
      : [
          need2fa?.tFaEmail ? 'Email' : null,
          need2fa?.tFaMobile ? 'Mobile' : null,
          need2fa?.tFaGoogle ? 'Google' : null,
        ].filter((method): method is NonNullable<typeof method> => !!method);
    const validSupportOtpMethod = supportOtpMethod.filter(method => method !== 'None');
    const fallbackOtpMethod = validSupportOtpMethod.includes('Email')
      ? 'Email'
      : validSupportOtpMethod[0] || 'Email';
    const defaultOtpMethod =
      need2fa?.defaultOtpMethod &&
      need2fa.defaultOtpMethod !== 'None' &&
      validSupportOtpMethod.includes(need2fa.defaultOtpMethod)
        ? need2fa.defaultOtpMethod
        : fallbackOtpMethod;

    switch (defaultOtpMethod) {
      case 'Mobile':
        return 'Mobile';
      case 'Google':
        return 'AuthGoogle';
      case 'Email':
      case 'None':
      default:
        return 'Email';
    }
  }

  private setLogin2faText(sendType: SendAction): void {
    switch (sendType) {
      case 'Mobile':
        this.login2faHeader = 'Mobile Verification';
        this.login2faSubmitText = 'Confirm Mobile';
        break;
      case 'AuthGoogle':
        this.login2faHeader = 'Authenticator Verification';
        this.login2faSubmitText = 'Confirm Code';
        break;
      case 'Email':
      default:
        this.login2faHeader = 'Email Verification';
        this.login2faSubmitText = 'Confirm Email';
        break;
    }
  }
}
